import { useEffect, useRef, useState } from 'react';

export type TSaveStatus = 'saving' | 'saved' | 'error';

interface UseFieldSaveStatusOptions {
  savedStatusTimeoutMs: number;
}

/**
 * Tracks a per-field save status (`saving` / `saved` / `error`) and error
 * message for an autosave form with multiple independent fields, keyed by
 * field name. `saved` auto-clears itself after `savedStatusTimeoutMs`.
 */
export function useFieldSaveStatus({
  savedStatusTimeoutMs,
}: UseFieldSaveStatusOptions) {
  const [status, setStatus] = useState<Record<string, TSaveStatus>>({});
  const [errorMessage, setErrorMessage] = useState<Record<string, string>>({});
  const savedStatusTimers = useRef<
    Record<string, ReturnType<typeof setTimeout>>
  >({});

  useEffect(() => {
    const timers = savedStatusTimers.current;
    return () => {
      Object.values(timers).forEach(clearTimeout);
    };
  }, []);

  const markSaving = (field: string) => {
    clearTimeout(savedStatusTimers.current[field]);
    setStatus((prev) => ({ ...prev, [field]: 'saving' }));
    setErrorMessage((prev) => ({ ...prev, [field]: '' }));
  };

  const markSaved = (field: string) => {
    setStatus((prev) => ({ ...prev, [field]: 'saved' }));

    savedStatusTimers.current[field] = setTimeout(() => {
      setStatus((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }, savedStatusTimeoutMs);
  };

  const markError = (field: string, message: string) => {
    setStatus((prev) => ({ ...prev, [field]: 'error' }));
    setErrorMessage((prev) => ({ ...prev, [field]: message }));
  };

  return { status, errorMessage, markSaving, markSaved, markError };
}
